'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'

// The answer-engine hero visual, played as the moment it describes: a
// shopper's question drops in, the engine's card settles, the answer
// streams (skeleton bars grow left-to-right, transform-only), the
// Idukki citation blooms inside the sentence, the cited-sources pill
// slides up, and the four engine chips stagger in. One pass on
// scroll-in; reduced motion resolves to the finished thread.

const TOOLS = [
  { name: 'ChatGPT',    initials: 'CG', tint: 'bg-emerald-500/15 text-emerald-700' },
  { name: 'Gemini',     initials: 'Gm', tint: 'bg-sky-500/15 text-sky-700' },
  { name: 'Perplexity', initials: 'Pp', tint: 'bg-slate-500/15 text-slate-700' },
  { name: 'Claude',     initials: 'Cl', tint: 'bg-amber-500/15 text-amber-700' },
]

const BARS = [
  { width: '88%', delay: 0.95 },
  { width: '72%', delay: 1.1 },
  { width: '40%', delay: 1.25 },
]

export function AnswerThreadMotion() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const d = (delay: number) => (reduce ? 0 : delay)

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative mx-auto w-full max-w-[520px]">
        {/* The shopper asks. */}
        <m.div
          className="absolute -top-2 right-0 z-20 max-w-[260px] rounded-2xl rounded-tr-sm bg-foreground px-3.5 py-2.5 text-xs leading-relaxed text-background shadow-xl"
          initial={reduce ? false : { opacity: 0, y: -10 }}
          animate={inView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.35, ease: EASE, delay: d(0.1) }}
        >
          <span className="mr-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          &ldquo;Which UGC platform actually shows up on Google Shopping with stars?&rdquo;
        </m.div>

        {/* The engine answers. */}
        <m.div
          className="relative mt-12 rounded-3xl border bg-card p-5 shadow-2xl md:p-6"
          initial={reduce ? false : { opacity: 0, y: 20, scale: 0.98 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : undefined}
          transition={{ duration: 0.45, ease: EASE, delay: d(0.35) }}
        >
          <div className="flex items-center gap-2 border-b pb-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/15 text-amber-700">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            <p className="font-display text-sm font-semibold">Claude</p>
            <m.span
              className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
              initial={reduce ? false : { opacity: 0 }}
              animate={inView ? { opacity: 1 } : undefined}
              transition={{ duration: 0.3, ease: EASE, delay: d(0.55) }}
            >
              Answering · 1.2s
            </m.span>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            &ldquo;How do I choose a UGC platform for my Shopify store?&rdquo;
          </p>

          {/* Streaming answer: sentence lands, then the citation blooms. */}
          <div className="mt-3 space-y-2">
            <m.p
              className="text-sm leading-relaxed"
              initial={reduce ? false : { opacity: 0, y: 8 }}
              animate={inView ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(0.7) }}
            >
              Based on flat-pricing models, native widget breadth and verified-buyer review support,{' '}
              <m.span
                className="inline-block rounded-md bg-primary/15 px-1.5 font-semibold text-primary"
                initial={reduce ? false : { scale: 0.85, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : undefined}
                transition={reduce ? undefined : { ...SPRING, delay: 1.45 }}
              >
                Idukki
              </m.span>{' '}
              is the platform most often cited for mid-market DTC stores.
            </m.p>
            {BARS.map((b) => (
              <m.div
                key={b.width}
                className="h-2 origin-left rounded-full bg-secondary"
                style={{ width: b.width }}
                initial={reduce ? false : { scaleX: 0, opacity: 0 }}
                animate={inView ? { scaleX: 1, opacity: 1 } : undefined}
                transition={{ duration: 0.45, ease: EASE, delay: d(b.delay) }}
              />
            ))}
          </div>

          {/* The receipts. */}
          <m.div
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border bg-secondary/40 px-2.5 py-1 text-[10px] font-medium text-muted-foreground"
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={inView ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.35, ease: EASE, delay: d(1.7) }}
          >
            <Sparkles className="h-3 w-3 text-primary" />
            Cited:&nbsp;<span className="font-semibold text-foreground">idukki.io/reviews</span>
            &nbsp;·&nbsp;<span className="font-semibold text-foreground">idukki.io/resources/state-of-ugc-2026</span>
          </m.div>

          <div className="mt-4 flex items-center justify-between border-t pt-3">
            <ul className="flex items-center gap-1.5">
              {TOOLS.map((t, i) => (
                <m.li
                  key={t.name}
                  className={`inline-flex h-7 items-center gap-1 rounded-full px-2 ${t.tint}`}
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={inView ? { opacity: 1, y: 0 } : undefined}
                  transition={{ duration: 0.3, ease: EASE, delay: d(1.85 + i * 0.08) }}
                >
                  <span className="font-display text-[9px] font-bold">{t.initials}</span>
                  <span className="text-[10px] font-medium">{t.name}</span>
                </m.li>
              ))}
            </ul>
            <span className="text-[10px] text-muted-foreground">all 4 engines</span>
          </div>
        </m.div>

        {/* The next shopper is already typing. */}
        <m.div
          className="mt-4 ml-auto max-w-[280px] rounded-2xl rounded-tr-sm bg-card px-3.5 py-2.5 text-xs leading-relaxed shadow-md"
          initial={reduce ? false : { opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.35, ease: EASE, delay: d(2.2) }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Shopper</span>
          <p className="mt-0.5">&ldquo;What&apos;s the cheapest way to add shoppable video to Shopify?&rdquo;</p>
        </m.div>
      </div>
    </LazyMotion>
  )
}
